import { createFileRoute, redirect } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { DocumentHead } from '@/components/core/DocumentHead';
import Header from '@/components/Header/Header';
import { CenteredColumn } from '@/components/CenteredColumn';
import { FullsizePage } from '@/components/FullsizePage';
import { StudentPortalInfo } from '@/components/PortalInfo/StudentPortalInfo';
import { getTenantCardCreditLimit } from '@/constants/tenantCardCreditLimits';
import { useStudentsDisabled } from '@/contexts/ConfigContext';
import { useBranding } from '@/hooks/useBranding';

export const Route = createFileRoute('/$tenantId/_layout/portal-info/')({
  component: PortalInfo,
  beforeLoad: ({ params }) => {
    const hasPermission = checkUserPermissions({
      permission: 'tenant:viewer',
    });
    if (!hasPermission) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function PortalInfo() {
  const { tenantId } = Route.useParams();
  const { name } = useBranding();
  const studentsDisabled = useStudentsDisabled();

  return (
    <FullsizePage>
      <DocumentHead
        titleKey="Portalinformation"
        descriptionKey="Information om hur portalen fungerar som kan vara bra att veta."
      />
      <Header title={<FormattedMessage defaultMessage="Portalinformation" />} />
      <CenteredColumn>
        <div className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-300 bg-background-50 p-6">
            <h2 className="mb-4 text-lg font-semibold text-text-900">
              <FormattedMessage defaultMessage="Bra att veta om portalen" />
            </h2>
            <i className="mb-4 block text-text-700">
              <FormattedMessage defaultMessage="Denna sida visas enbart för systemadministratörer." />
            </i>
            <ul className="flex list-disc flex-col gap-2 pl-4">
              <li>
                <FormattedMessage defaultMessage="Det är extremt viktigt att man inte modifierar någon data på kort som nyttjas som tjänsteresa på kort, då vår koppling mot kortet och dessa data kan förstöras. Kort som tas bort som tjänsteresa på kort korrekt via portalen kan nyttjas för annat igen, inklusive tjänsteresa på kort, utan att något behöver göras i BobCat." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Ändra inte portal-relaterad data i BobCat/CRM, då detta kan leda till felaktiga uppgifter i portalen och onödiga problem/supportärenden." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Månadsavstämningarna sker första vardagen varje månad. Alla fakturamottagare måste vara godkända innan avstämningen kan ske, så det är extremt viktigt att dessa åtgärdas så fort de kommer in (vilket är anledningen till att man kan få email om dessa, genom profilinställningarna)." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Vilka erbjudanden/produkter som finns tillgängliga synkas mot BobCat varje natt, så man måste vänta till nästa dag innan nya produktutbudet visas." />
              </li>
              <li>
                <FormattedMessage
                  defaultMessage="För tjänsteresa på kort finns det begränsningar i BobCat hur mycket tillgodobelopp som får finnas på ett kort. För er ({tenantName}) är gränsen: {limit} SEK. Portalen har inget sätt att hämta denna information ifall den ändras utan detta måste meddelas manuellt till oss."
                  values={{
                    limit: getTenantCardCreditLimit(tenantId),
                    tenantName: name,
                  }}
                />
              </li>
              <li>
                <FormattedMessage defaultMessage="Då status för varje biljett måste synkas mellan denna portalen och BobCat kan det ta upp till 5 minuter innan en biljett som ska raderas faktiskt raderas i BobCat." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Om en avdelning har gett sig själv egen faktureringsinformation så kan man i dagsläget inte ändra tillbaka till att nyttja faktureringsuppgifter från ovanliggande avdelning." />
              </li>
              <li>
                <FormattedMessage defaultMessage="Tjänsteresa på kort är implementerat genom att kunden får ett kort kopplat till en bobcat wallet med tillgodo-pengar i. Om man gett kortet 500kr i kredit så fyller vi alltså på kortets bobcat wallet med 500kr vid skapandet av Tjänsteresa på Kort. " />
                <br />
                <FormattedMessage defaultMessage="När kunden betalar med kortet så dras beloppet från kortets wallet. På morgonen den 1:a i varje månad, läser vi av hur mycket av tillgodo-pengarna som finns kvar i walleten, skapar en transaktion på mellanskillnaden, och toppar sedan upp beloppet tilllbaka till 500kr. " />
                <br />
                <FormattedMessage defaultMessage="Transaktionen skapas alltså den 1:a i månaden, men märks med föregående månad som faktureringsperiod. Avstämningen fakturerar alla transaktioner för föregående månad och räknar då in transaktionen utifrån faktureringsperioden istället för skapandedatumet, så den faktureras direkt efter månaden den gäller. " />
                <br />
                <FormattedMessage defaultMessage="Faktureringen av en månad väntar tills alla kort har stämts av, så att ingen transaktion för månaden blir kvar till nästa faktura." />
              </li>
            </ul>
          </section>
          {!studentsDisabled && <StudentPortalInfo />}
        </div>
      </CenteredColumn>
    </FullsizePage>
  );
}
